import React, { useEffect } from "react";
import {
  Input,
  Button,
  FormFeedback,
  Col,
  Row,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
} from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { NAME_MAXLENGTH } from "@constants/constants";
import Spinners from "@components/custom-table/Spinner";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PHONE_NUMBER_MAXLENGTH } from "@constants/constants";
import { districtSchema } from "../../../helpers/validation-schema";
import { resetError, resetState } from "../controller/district-slice";
import {
  addDistrict,
  editDistrict,
  getDistrict,
  getDistrictCount,
} from "../services/district-services";
import Select from "react-select";
import { getStateList } from "../../state/services.js/state-services";
import CustomLabel from "../../../components/CustomLabel";
import { NAME_REGEX } from "../../../constants/constants";
import { handleInput } from "../../../helpers/helpers";

export const AddDistrict = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const route = pathname.split("/")[2];
  const districtId = pathname.split("/")[3];
  const success = useSelector((state) => state.district.success);
  const district = useSelector((state) => state.district.districts);
  const loading = useSelector((state) => state.district.loading);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pageQuery = parseInt(searchParams.get("current_page"));
  const successMessage = useSelector((state) => state.district.successMessage);
  const errorMessage = useSelector((state) => state.district.errorMessage);
  const states = useSelector((state) => state.state.states);
  const stateOptions = states.map((state) => ({
    value: state._id,
    label: state.name,
  }));

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(districtSchema),
  });

  useEffect(() => {
    if (route === "add") {
      async function fetchCount() {
        const count = await getDistrictCount();
        setValue("sorting", count + 1);
      }
      fetchCount();
    }
  }, [route, setValue]);

  useEffect(() => {
    dispatch(getStateList({ pageCount: 1, limit: 100 }));
  }, []);

  useEffect(() => {
    if (route !== "add") {
      dispatch(getDistrict(districtId));
    }
  }, [districtId, dispatch, route]);

  useEffect(() => {
    if (success) {
      navigate(`/district?current_page=${pageQuery}`);
      dispatch(resetState());
    }
  }, [success, dispatch, navigate, successMessage, pageQuery]);

  useEffect(() => {
    if (errorMessage !== null) {
      toast.error(errorMessage?.message);

      errorMessage.data.items.forEach((item) => {
        const fieldName = Object.keys(item)[0];
        const message = item[fieldName];
        setError(fieldName, { type: "manual", message });
      });
    }
  }, [errorMessage, setError]);

  useEffect(() => {
    if (district) {
      setValue("name", district?.name);
      setValue("state_id", district?.state_id);
      setValue("sorting", district?.sorting);
    }
  }, [district, setValue]);

  const onSubmit = (data) => {
    if (route !== "edit") {
      const formattedData = {
        ...data,
        sorting: parseInt(data.sorting),
      };
      dispatch(addDistrict(formattedData));
    } else {
      const formattedData = {
        ...data,
        sorting: parseInt(data.sorting),
      };

      dispatch(editDistrict({ id: districtId, data: formattedData }));
    }
  };
  const customStyles = {
    control: (provided) => ({
      ...provided,
      borderColor: errors.state_id ? "red" : provided.borderColor,
      boxShadow: "none",
      "&:hover": {
        borderColor: errors.state_id ? "red" : provided.borderColor,
        boxShadow: "none",
      },
    }),
  };
  const handleInputChange = (event, field) => {
    const { value } = event.target;
    const formattedValue = value.replace(/\D/g, "");
    setValue(field, formattedValue);
  };
  return (
    <div className="page-content">
      {loading ? (
        <Spinners />
      ) : (
        <>
          <ToastContainer autoClose={2000} />
          <Card>
            <CardBody className="text-start">
              <CardTitle className="mb-4 my-3">
                {route === "edit"
                  ? "Edit District"
                  : route === "view"
                  ? "View District"
                  : "Create District"}
              </CardTitle>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Sorting" isRequired>
                        Sorting
                      </CustomLabel>
                      <Controller
                        name="sorting"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Sorting"
                              invalid={!!errors.sorting}
                              maxLength={PHONE_NUMBER_MAXLENGTH}
                              onChange={(e) => handleInputChange(e, "sorting")}
                            />
                            {errors.sorting && (
                              <FormFeedback>
                                {errors.sorting.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="State" isRequired>
                        State
                      </CustomLabel>
                      <Controller
                        name="state_id"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Select
                              {...field}
                              options={stateOptions}
                              placeholder="Select State"
                              onChange={(selectedOption) =>
                                field.onChange(
                                  selectedOption ? selectedOption.value : ""
                                )
                              }
                              value={stateOptions.find(
                                (option) => option.value === field.value
                              )}
                              styles={customStyles}
                              isClearable
                              menuPlacement="auto"
                              menuShouldScrollIntoView={true}
                              minMenuHeight={150}
                              maxMenuHeight={150}
                              isMenuScrollable={true}
                            />
                            {errors.state_id && (
                              <FormFeedback className="d-block">
                                {errors.state_id.message}
                              </FormFeedback>
                            )}
                          </>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                {/* <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="State">State</Label>
                          <Controller
                            name="state_id"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                options={stateOptions}
                                placeholder="Select State"
                              />
                            )}
                          />
                        </FormGroup>
                      </Col>
                    </Row> */}
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="District Name" isRequired>
                        District Name
                      </CustomLabel>
                      <Controller
                        name="name"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Enter District Name"
                              invalid={!!errors.name}
                              onInput={(e) => handleInput(e, NAME_REGEX)}
                              maxLength={NAME_MAXLENGTH}
                            />
                            {errors.name && (
                              <FormFeedback>{errors.name.message}</FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}></Col>
                  <Col md={6}>
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => {
                          navigate(`/district?current_page=${pageQuery}`);
                          dispatch(resetError());
                        }}
                      >
                        {route === "view" ? "Back" : "Cancel"}
                      </Button>
                      {route !== "view" ? (
                        <Button type="submit" color="primary">
                          {route === "edit"
                            ? "Edit District"
                            : "Create District"}
                        </Button>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

export default AddDistrict;
